<script setup lang="ts">

import AppContainer from '@apparatix/library/AppContainer.vue';
import Avatar from 'primevue/avatar';
import Badge from 'primevue/badge';
import Button from 'primevue/button';
import Column from 'primevue/column';
import ColumnDisplaySelector from '@apparatix/library/ColumnDisplaySelector.vue';
import DataTable, { type DataTableFilterEvent, type DataTablePageEvent, type DataTableRowClickEvent, type DataTableSortEvent } from 'primevue/datatable';
import DollarIcon from '@primeicons/vue/dollar';
import FileExportIcon from '@primeicons/vue/file-export';
import HeightOptimizer from '@apparatix/library/HeightOptimizer.vue';
import InfoCircleIcon from '@primeicons/vue/info-circle';
import InputText from 'primevue/inputtext';
import MapMarkerIcon from '@primeicons/vue/map-marker';
import MultiSelect from '@apparatix/library/MultiSelect.vue';
import PencilIcon from '@primeicons/vue/pencil';
import PricingBulkEditModal from '@apparatix/Components/PricingBulkEditModal.vue';
import RefreshIcon from '@primeicons/vue/refresh';
import Select from 'primevue/select';
import Tag from 'primevue/tag';
import ToggleSwitch from 'primevue/toggleswitch';
import {
  computed, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { FilterMatchMode } from '@primevue/core/api';
import {
  useBuildDefaultColumns, useColumnConfig, useEndpoint, useKeepAliveActive, useToastNotifications,
} from '@apparatix/composables';
import { CustomEventNames } from '@apparatix/constants/CustomEventNames';
import { Section } from '@apparatix/library/core';
import Typography from '@apparatix/library/core/typography/Typography.ts';
import {
  pricingDescriptions,
  usePricingStore,
  type PricingComponentAdmin,
  type PricingComponentOption,
  type PricingRow,
  type SalesContextCode,
  type SelectOption,
} from '@apparatix/stores/usePricingStore';
import { CONTEXT_STYLES, formatCurrency } from '@apparatix/utilities/pricingFormatting';

defineOptions({ name: 'ManagePricing' });

const pricingStore = usePricingStore();
const {
  rows,
  totalRecords,
  componentOptions,
  contextOptions,
  venueOptions,
  statusOptions,
  canViewFloor,
  adminToolsEnabled,
  pricingComponents,
  originalPricingComponents,
  context,
  rowsPerPage,
  firstRecord,
  tableSortField,
  tableSortOrder,
  tableFilters,
  selectedRows,
  selectAllMatchingFilter,
} = storeToRefs(pricingStore);

const contextStyle = computed(() => CONTEXT_STYLES[context.value]);

const router = useRouter();
const route = useRoute();
const { isKeepAliveActive } = useKeepAliveActive();

const onRowClick = (event: DataTableRowClickEvent) => {
  const row = event.data as PricingRow;
  const base = (route.meta?.basePath as string) ?? '/pricing';

  router.push({
    path: `${base}/detail/inventory/${row.inv_id}/pricing`,
    query: { invNumber: row.inv_number },
  });
};

// "Select all matching filter" is never materialized as a client-side id list (the matched set
// can span thousands of rows across pages) — it's just a flag the bulk-edit endpoints resolve
// server-side against the same filters/context the grid is currently showing.
const isBulkEditModalVisible = ref(false);
const showSelectAllBanner = computed(() => !selectAllMatchingFilter.value
  && rows.value.length > 0
  && selectedRows.value.length === rows.value.length);

// The page's own selection already covers every matching row once the two counts agree — no
// further "select all matching filter" action is possible, so the button offering it is a dead
// end rather than a shortcut.
const hasMoreRowsToSelect = computed(() => totalRecords.value > rows.value.length);

const selectAllMatching = () => {
  selectAllMatchingFilter.value = true;
};

watch([tableFilters, context], () => {
  selectAllMatchingFilter.value = false;
});

const selectionDescriptor = computed(() => (selectAllMatchingFilter.value
  ? { mode: 'allMatchingFilter' as const }
  : { mode: 'ids' as const, ids: selectedRows.value.map(row => row.inv_id) }));

const onBulkEditCompleted = () => {
  isBulkEditModalVisible.value = false;
  selectedRows.value = [];
  selectAllMatchingFilter.value = false;
  refresh();
};

type PricingTableResponse = {
  data?: { rows?: PricingRow[] };
  pagination?: { total?: number; perPage?: number; currentPage?: number };
};

type PricingOptionsResponse = {
  data?: {
    components?: PricingComponentOption[];
    contexts?: { code: SalesContextCode; name: string }[];
    venues?: SelectOption[];
    statuses?: SelectOption[];
    permissions?: { canViewFloor?: boolean; hasAdminTools?: boolean };
  };
};

const dt = ref();
const isTableLoading = ref(false);

// A never-priced component has no pricing_profiles row (null), which is shown
// as $0.00 here — a display default, not a persisted value (see
// InventoryPricingTab.vue for the equivalent flyout-side default).
const formatAmount = (value: string | null | undefined) => formatCurrency(value);

// Price columns (per-component and per-component floor) are numeric searches:
// Contains (default), Less Than, Greater Than — mirrors ManageMeasurements'
// "value" column.
const NUMERIC_MATCH_MODES = [
  { label: 'Contains', value: 'contains' },
  { label: 'Less Than', value: 'lt' },
  { label: 'Greater Than', value: 'gt' },
];

// The 3 inventory fields are static; the pricing-component columns are
// sourced from componentOptions (fetched from /manage-pricing/options, which
// reads pricing_components.key/.label/.has_floor) so labels stay in sync with
// the DB and newly-added components become available without a code change.
// Each component's Floor column is inserted immediately after its own column
// (not appended at the end) and only when it has one and the caller can view it.
const defaultColumns = computed(() => useBuildDefaultColumns([
  ['inv_number', 'Number'],
  ['inv_status', 'Status'],
  ['inv_type', 'Type'],
  ...componentOptions.value.flatMap(component => [
    [component.key, component.label] as [string, string],
    ...(component.hasFloor && canViewFloor.value
      ? [[`${component.key}_floor`, `${component.label} Floor`] as [string, string]]
      : []),
  ]),
]));

const {
  handleColumnOrderChange, handleVisibleColumnChange, visibleColumns, columnOrder,
} = useColumnConfig({
  visibleItemsKey: 'pricing:manage:visible-columns',
  columnOrderKey: 'pricing:manage:column-order',
  ref: dt,
  defaultColumns,
});

// The checkbox column isn't part of defaultColumns/columnOrder (it's not user-hideable or
// reorderable), so PrimeVue's reorderableColumns treats it as unmatched against d_columnOrder
// and appends it after every known column instead of leaving it in its templated first
// position. Keep it pinned at the front of d_columnOrder directly, matching how the checkbox
// Column itself is keyed via column-key="__select" below.
const SELECTION_COLUMN_KEY = '__select';

watch(() => dt.value?.d_columnOrder, (order) => {
  if (order && !order.includes(SELECTION_COLUMN_KEY)) {
    // eslint-disable-next-line camelcase
    dt.value.d_columnOrder = [SELECTION_COLUMN_KEY, ...order];
  }
});

const rowsPerPageOptions = computed(() => {
  const base = [100, 250, 500, 1000];
  const options = totalRecords.value > base[base.length - 1]
    ? [...base, totalRecords.value]
    : base;
  return [...new Set(options)].sort((a, b) => a - b);
});

const { getResults: fetchOptions } = useEndpoint<PricingOptionsResponse>({
  type: 'GET',
  url: '/manage-pricing/options',
  successField: 'data',
  toast: { showOnSuccess: false },
});

const { getResults: fetchPricingTable } = useEndpoint<PricingTableResponse>({
  type: 'POST',
  url: '/manage-pricing/table',
  successField: 'data',
  cancelPreviousRequestOnSubsequent: true,
  toast: { showOnSuccess: false },
});

const loadOptions = async () => {
  const response = await fetchOptions();

  if (!response?.data) {
    return;
  }

  componentOptions.value = response.data.components ?? [];
  contextOptions.value = response.data.contexts ?? [];
  venueOptions.value = response.data.venues ?? [];
  statusOptions.value = response.data.statuses ?? [];
  canViewFloor.value = response.data.permissions?.canViewFloor ?? false;
  adminToolsEnabled.value = response.data.permissions?.hasAdminTools ?? false;

  // Price columns are only known once componentOptions/canViewFloor load, so their
  // numeric filter entries are added here rather than in buildDefaultFilters().
  const numericKeys = [
    ...componentOptions.value.map(component => component.key),
    ...(canViewFloor.value
      ? componentOptions.value.filter(component => component.hasFloor).map(component => `${component.key}_floor`)
      : []),
  ];

  numericKeys.forEach(key => {
    if (!(key in tableFilters.value)) {
      tableFilters.value[key] = { value: null, matchMode: FilterMatchMode.CONTAINS };
    }
  });
};

const loadPricingTable = async (page: number) => {
  isTableLoading.value = true;

  const response = await fetchPricingTable({
    page,
    pageSize: rowsPerPage.value,
    sortField: tableSortField.value,
    sortOrder: tableSortOrder.value,
    filters: tableFilters.value,
    context: context.value,
  });

  if (response) {
    rows.value = response.data?.rows ?? [];
    totalRecords.value = response.pagination?.total ?? 0;
  }

  isTableLoading.value = false;
};

// Derive the 1-based page from the persisted paginator offset so the table
// resumes on the same page after a navigation round-trip.
const currentPage = () => Math.floor(firstRecord.value / rowsPerPage.value) + 1;

const onContextChange = () => {
  firstRecord.value = 0;
  loadPricingTable(1);
};

const onPage = (event: DataTablePageEvent) => {
  rowsPerPage.value = event.rows;
  firstRecord.value = event.first;
  loadPricingTable(currentPage());
};

const onSort = (event: DataTableSortEvent) => {
  tableSortField.value = (event.sortField as string) ?? null;
  tableSortOrder.value = event.sortOrder ?? null;
  firstRecord.value = 0;
  loadPricingTable(1);
};

const FILTER_DEBOUNCE_MS = 400;
let filterDebounceTimer: number | undefined;

const onFilter = (event: DataTableFilterEvent) => {
  tableFilters.value = { ...(event.filters as typeof tableFilters.value) };
  firstRecord.value = 0;

  if (filterDebounceTimer) {
    window.clearTimeout(filterDebounceTimer);
  }

  filterDebounceTimer = window.setTimeout(() => loadPricingTable(1), FILTER_DEBOUNCE_MS);
};

// PrimeVue's CSV export reads each column's raw field straight off the row (e.g. row.ad_space),
// but prices/floors live nested under row.prices/row.floors — so the native export would come out
// blank for every cell. Build a flattened, pre-formatted copy of the current page so the export
// matches the grid's on-screen display (including the "$0.00" default for an unset rate).
const exportCsv = () => {
  const exportRows = rows.value.map(row => {
    const formattedPrices = Object.fromEntries(
      componentOptions.value.map(component => [component.key, formatAmount(row.prices?.[component.key])]),
    );

    const formattedFloors = canViewFloor.value
      ? Object.fromEntries(
        componentOptions.value
          .filter(component => component.hasFloor)
          .map(component => [`${component.key}_floor`, formatAmount(row.floors?.[component.key])]),
      )
      : {};

    return { ...row, ...formattedPrices, ...formattedFloors };
  });

  dt.value.exportCSV(undefined, exportRows);
};

//
// Admin: pricing component management (enable/disable, edit label, view has_floor)
//

type PricingComponentsResponse = {
  data?: { components?: PricingComponentAdmin[] };
};

type UpdatePricingComponentResponse = {
  data?: { component?: PricingComponentAdmin };
};

type UpdatePricingComponentError = {
  message?: string;
};

const isLoadingComponents = ref(false);
const { showError } = useToastNotifications();

const { getResults: fetchPricingComponents } = useEndpoint<PricingComponentsResponse>({
  type: 'GET',
  url: '/manage-pricing/components',
  successField: 'data',
  toast: { showOnSuccess: false },
});

// toast: { showOnSuccess: false } fully replaces useEndpoint's toast defaults rather than
// merging, so showOnError must be spelled out here too — otherwise the 422 sent when trying
// to disable a yield-engine-locked component (see PricingController::updateComponent) would
// fail silently. The default error toast can't show that response's specific message (it only
// takes a static configured string), so onError surfaces it directly instead.
const {
  getResults: updatePricingComponentRequest,
  setEndpointUrl: setUpdatePricingComponentEndpoint,
  isLoading: isSavingComponents,
} = useEndpoint<UpdatePricingComponentResponse, UpdatePricingComponentError>({
  url: '',
  type: 'PUT',
  successField: 'data',
  toast: { showOnSuccess: false, showOnError: false },
  onError: errorData => showError(errorData?.message ?? 'Failed to save pricing component changes.'),
});

// Deep-cloned into both the working copy and the dirty-check baseline so edits in
// pricingComponents never mutate originalPricingComponents.
const applyPricingComponents = (components: PricingComponentAdmin[]) => {
  pricingComponents.value = structuredClone(components);
  originalPricingComponents.value = structuredClone(components);
};

const dirtyComponents = () => pricingComponents.value.filter((component, index) => {
  const original = originalPricingComponents.value[index];
  return !original || component.label !== original.label || component.isEnabled !== original.isEnabled;
});

const componentsDirty = computed(() => dirtyComponents().length > 0);

const loadPricingComponents = async () => {
  isLoadingComponents.value = true;

  const response = await fetchPricingComponents();

  if (response?.data?.components) {
    applyPricingComponents(response.data.components);
  }

  isLoadingComponents.value = false;
};

const saveComponentChanges = async () => {
  if (!componentsDirty.value || isSavingComponents.value) {
    return;
  }

  // Saved one at a time (not Promise.all) because updatePricingComponentRequest's
  // isLoading/endpointUrl are shared refs on a single useEndpoint instance — concurrent
  // calls would have the first response to land flip isSavingComponents back to false
  // while others are still in flight.
  for (const component of dirtyComponents()) {
    setUpdatePricingComponentEndpoint(`/manage-pricing/components/${component.id}`);

    await updatePricingComponentRequest<UpdatePricingComponentResponse, { label: string; is_enabled: boolean }>({
      label: component.label,
      // eslint-disable-next-line camelcase
      is_enabled: component.isEnabled,
    });
  }

  // Component labels/enabled state affect the main grid's columns, so refresh
  // both the grid's options and the admin table's baseline together.
  await loadOptions();
  await loadPricingComponents();
};

// Re-pull data on demand, keeping the current selection / filters / sort / page.
const refresh = async () => {
  await loadOptions();
  await loadPricingTable(currentPage());

  if (adminToolsEnabled.value) {
    await loadPricingComponents();
  }
};

onMounted(async () => {
  // Re-clicking the active "Manage Pricing" sidebar item dispatches this
  // event (see SidebarItemList.vue); registered before the initialized guard
  // so it stays wired up when returning to an already-loaded screen.
  window.addEventListener(CustomEventNames.pricing.reloadHome, refresh);

  // State lives in the Pinia store and survives this component being unmounted
  // when the user navigates away, so only fetch on the first visit. Use the
  // Refresh button to re-pull on demand.
  if (pricingStore.initialized) {
    return;
  }

  await loadOptions();
  await loadPricingTable(currentPage());

  if (adminToolsEnabled.value) {
    await loadPricingComponents();
  }

  pricingStore.initialized = true;
});

onBeforeUnmount(() => window.removeEventListener(CustomEventNames.pricing.reloadHome, refresh));

</script>

<template>
  <AppContainer>
    <div class="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div class="flex items-center gap-2">
        <Avatar
          size="normal"
          shape="circle"
          style="background-color: var(--p-blue-100); color: var(--p-blue-500);"
        >
          <template #icon>
            <DollarIcon />
          </template>
        </Avatar>
        <h1 class="text-lg">
          Manage Pricing
        </h1>
        <Badge :value="totalRecords" size="large" severity="secondary" />
      </div>
      <div class="flex flex-col gap-4 sm:flex-row sm:gap-4">
        <Button
          v-if="selectedRows.length > 0 || selectAllMatchingFilter"
          severity="primary"
          class="shrink-0 whitespace-nowrap"
          @click="isBulkEditModalVisible = true"
        >
          <PencilIcon />
          <span class="font-bold">
            Bulk Edit ({{ selectAllMatchingFilter ? totalRecords : selectedRows.length }})
          </span>
        </Button>
        <Button
          v-tooltip.left="'Refresh'"
          outlined
          severity="secondary"
          class="shrink-0 whitespace-nowrap"
          :loading="isTableLoading"
          @click="refresh()"
        >
          <RefreshIcon />
          <span class="font-bold">Refresh</span>
        </Button>
        <Button
          v-tooltip.left="'Export'"
          outlined
          severity="secondary"
          class="shrink-0 whitespace-nowrap"
          @click="exportCsv()"
        >
          <FileExportIcon />
          <span class="font-bold">Export</span>
        </Button>
      </div>
    </div>

    <div class="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div
        class="flex flex-wrap items-center gap-3 rounded-md border-l-4 px-3 py-2 transition-colors"
        :class="contextStyle.banner"
      >
        <span class="flex items-center gap-2 text-sm font-bold">
          <MapMarkerIcon />
          {{ contextStyle.label }}
          <InfoCircleIcon
            v-if="context === 'national'"
            v-tooltip="pricingDescriptions.automatedSales"
            color="var(--tw-text-gray-500)"
            class="cursor-pointer"
          />
        </span>
        <Select
          v-model="context"
          :options="contextOptions"
          option-label="name"
          option-value="code"
          class="w-48"
          @change="onContextChange"
        />
      </div>
      <div class="flex items-center justify-center gap-2">
        <ColumnDisplaySelector
          :default-table-columns="defaultColumns"
          :default-visible-columns="defaultColumns.filter(col => col.visible).map(col => col.field)"
          :table-columns="columnOrder"
          :visible-columns="visibleColumns"
          storage-prefix="pricing:manage"
          :data-table="dt"
          @update:visible-columns="handleVisibleColumnChange"
          @update:table-columns="handleColumnOrderChange"
        />
      </div>
    </div>

    <div v-if="showSelectAllBanner" class="mb-2 flex items-center gap-2 rounded-md bg-[var(--p-blue-50)] px-3 py-2 text-sm">
      <span>All {{ rows.length }} rows{{ hasMoreRowsToSelect ? ' on this page' : '' }} are selected.</span>
      <Button
        v-if="hasMoreRowsToSelect"
        link
        class="p-0 text-sm font-bold"
        @click="selectAllMatching"
      >
        Select all {{ totalRecords }} rows matching the current filter
      </Button>
    </div>

    <HeightOptimizer>
      <template #default="{ availableHeight }">
        <DataTable
          ref="dt"
          v-model:filters="tableFilters"
          v-model:selection="selectedRows"
          lazy
          :value="rows"
          :loading="isTableLoading"
          :total-records="totalRecords"
          :rows="rowsPerPage"
          :first="firstRecord"
          paginator
          :rows-per-page-options="rowsPerPageOptions"
          filter-display="row"
          data-key="inv_id"
          table-style="min-width: 70rem;"
          table-layout="fixed"
          scrollable
          :scroll-height="`calc(${availableHeight} - 60px)`"
          :virtual-scroller-options="{ itemSize: 50 }"
          reorderable-columns
          resizable-columns
          column-resize-mode="expand"
          row-hover
          :pt="{ bodyRow: { class: 'cursor-pointer' } }"
          @page="onPage"
          @sort="onSort"
          @filter="onFilter"
          @row-click="onRowClick"
        >
          <Column
            column-key="__select"
            selection-mode="multiple"
            header-style="width: 3rem"
            frozen
            :reorderable-column="false"
          />
          <Column
            field="inv_number"
            :header="defaultColumns.find(col => col.field === 'inv_number')?.header"
            sortable
            :hidden="!visibleColumns.includes('inv_number')"
          >
            <template #filter="{ filterModel, filterCallback }">
              <InputText
                v-model="filterModel.value"
                type="text"
                class="p-column-filter"
                placeholder="Search"
                style="min-width: 6rem; max-width: 10rem"
                @input="filterCallback()"
              />
            </template>
          </Column>
          <Column
            field="inv_status"
            :header="defaultColumns.find(col => col.field === 'inv_status')?.header"
            sortable
            :show-filter-menu="false"
            :hidden="!visibleColumns.includes('inv_status')"
          >
            <template #body="{ data }">
              {{ statusOptions.find(s => s.code === data.inv_status)?.name ?? data.inv_status ?? '' }}
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <MultiSelect
                v-model="filterModel.value"
                :options="statusOptions"
                option-label="name"
                option-value="code"
                placeholder="All"
                show-clear
                class="p-column-filter"
                style="min-width: 10rem"
                @change="filterCallback()"
              />
            </template>
          </Column>
          <Column
            field="inv_type"
            :header="defaultColumns.find(col => col.field === 'inv_type')?.header"
            sortable
            :show-filter-menu="false"
            :hidden="!visibleColumns.includes('inv_type')"
          >
            <template #body="{ data }">
              {{ venueOptions.find(v => v.code === data.inv_type)?.name ?? data.inv_type ?? '' }}
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <MultiSelect
                v-model="filterModel.value"
                :options="venueOptions"
                option-label="name"
                option-value="code"
                placeholder="All"
                show-clear
                class="p-column-filter"
                style="min-width: 10rem"
                @change="filterCallback()"
              />
            </template>
          </Column>
          <template v-for="component in componentOptions" :key="component.key">
            <Column
              :field="component.key"
              :header="component.label"
              sortable
              :filter-match-mode-options="NUMERIC_MATCH_MODES"
              :hidden="!visibleColumns.includes(component.key)"
            >
              <template #body="{ data }">
                {{ formatAmount((data as PricingRow).prices?.[component.key]) }}
              </template>
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Search"
                  style="min-width: 6rem; max-width: 10rem"
                  @input="filterCallback()"
                />
              </template>
            </Column>
            <!-- A component's Floor column, when it has one, renders immediately
                 after its own column rather than after every column. -->
            <Column
              v-if="component.hasFloor && canViewFloor"
              :field="`${component.key}_floor`"
              :export-header="defaultColumns.find(col => col.field === `${component.key}_floor`)?.header"
              sortable
              :filter-match-mode-options="NUMERIC_MATCH_MODES"
              :hidden="!visibleColumns.includes(`${component.key}_floor`)"
            >
              <template #header>
                <span
                  v-tooltip="pricingDescriptions.floorTooltip(component.key, component.label)"
                  class="p-datatable-column-title"
                >
                  {{ defaultColumns.find(col => col.field === `${component.key}_floor`)?.header }}
                </span>
              </template>
              <template #body="{ data }">
                {{ formatAmount((data as PricingRow).floors?.[component.key]) }}
              </template>
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Search"
                  style="min-width: 6rem; max-width: 10rem"
                  @input="filterCallback()"
                />
              </template>
            </Column>
          </template>
        </DataTable>
      </template>
    </HeightOptimizer>

    <div v-if="adminToolsEnabled" class="pt-10">
      <Section :footer="false">
        <Typography class="pb-2" variant="section-heading">
          Admin Tools
        </Typography>

        <div class="mb-4 max-w-3xl rounded-lg border p-3">
          <Typography class="pb-2" variant="section-subheading">
            Pricing Components
          </Typography>
          <Typography class="pb-3" variant="form-label">
            Enable or disable pricing components and edit their labels. Ad Space, Install, and Production
            power live yield pricing and cannot be disabled here. Has Floor is fixed per component and
            shown for reference only.
          </Typography>

          <DataTable
            :value="pricingComponents"
            :loading="isLoadingComponents"
            data-key="id"
            size="small"
            class="text-sm"
          >
            <Column field="key" header="Key" />
            <Column header="Label">
              <template #body="{ data }">
                <InputText v-model="data.label" class="w-full" />
              </template>
            </Column>
            <Column header="Has Floor">
              <template #body="{ data }">
                <Tag :value="data.hasFloor ? 'Yes' : 'No'" :severity="data.hasFloor ? 'info' : 'secondary'" />
              </template>
            </Column>
            <Column header="Enabled">
              <template #body="{ data }">
                <ToggleSwitch
                  v-model="data.isEnabled"
                  v-tooltip="data.isLocked ? 'Powers live yield pricing and cannot be disabled here.' : undefined"
                  :disabled="data.isLocked && data.isEnabled"
                />
              </template>
            </Column>
          </DataTable>

          <div class="flex items-center gap-3 pt-3">
            <Button
              size="small"
              :disabled="!componentsDirty || isSavingComponents"
              :loading="isSavingComponents"
              @click="saveComponentChanges"
            >
              <span class="font-bold">Save Changes</span>
            </Button>
            <span v-if="componentsDirty" class="text-sm text-yellow-600">Unsaved changes</span>
          </div>
        </div>
      </Section>
    </div>

    <PricingBulkEditModal
      :visible="isBulkEditModalVisible"
      :context="context"
      :component-options="componentOptions"
      :can-view-floor="canViewFloor"
      :filters="tableFilters"
      :selection="selectionDescriptor"
      @close="isBulkEditModalVisible = false"
      @completed="onBulkEditCompleted"
    />

    <RouterView v-if="isKeepAliveActive" />
  </AppContainer>
</template>

<style scoped>
.p-button-secondary {
  background-color: lightgray;
  background: var(--white);
  border: 1px solid var(--black-16);
  color: var(--black-80);
}

.p-badge-secondary {
  background-color: lightgray;
  background: var(--white);
  border: 1px solid var(--black-16);
  color: var(--black-80);
}
</style>
